import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const cartKey = (id, variant) => `${id}::${variant || ""}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("hafa_cart_v2");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("hafa_cart_v2", JSON.stringify(items));
  }, [items]);

  const removeItem = useCallback((id, variant) => {
    const key = cartKey(id, variant);
    setItems(prev => prev.filter(p => cartKey(p.id, p.variant) !== key));
  }, []);

  const addItem = useCallback((menuItem, variant = null, qty = 1) => {
    const key = cartKey(menuItem.id, variant);
    setItems(prev => {
      const existing = prev.find(p => cartKey(p.id, p.variant) === key);
      if (existing) {
        return prev.map(p => cartKey(p.id, p.variant) === key ? { ...p, quantity: p.quantity + qty } : p);
      }
      return [...prev, {
        id: menuItem.id,
        variant,
        name: menuItem.name,
        price: menuItem.price,
        quantity: qty,
      }];
    });
  }, []);

  const setQty = useCallback((id, variant, qty) => {
    if (qty <= 0) { removeItem(id, variant); return; }
    const key = cartKey(id, variant);
    setItems(prev => prev.map(p => cartKey(p.id, p.variant) === key ? { ...p, quantity: qty } : p));
  }, [removeItem]);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal, total: subtotal, count };
  }, [items]);

  const value = useMemo(() => ({
    items, addItem, setQty, removeItem, clear, totals, isOpen, setIsOpen, cartKey,
  }), [items, addItem, setQty, removeItem, clear, totals, isOpen]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
export { cartKey };
